import React, { useMemo, useState } from 'react';
import {
    Bar,
    BarChart,
    CartesianGrid,
    LabelList,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import {
    CompanySizeSeniorityRow,
    Seniority,
} from '../types';
import { SENIORITY_ORDER } from '../data/constants';
import { useMediaQuery } from '../hooks/useMediaQuery';
import {
    buildCompanyInsight,
    sortCompanySizeRows,
} from '../utils/dataTransforms';
import {
    formatCompactSalary,
    formatSalary,
    formatSample,
} from '../utils/formatters';
import AccessibleDataTable from './AccessibleDataTable';
import ChartTooltip from './ChartTooltip';

interface CompanySizeImpactProps {
    rows: CompanySizeSeniorityRow[];
    selectedYear: number;
}

const CompanyTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) {
        return null;
    }

    const row = payload[0].payload as CompanySizeSeniorityRow;

    return (
        <ChartTooltip
            title={row.companySize}
            rows={[
                { label: 'Kıdem', value: row.seniority },
                {
                    label: 'Medyan maaş',
                    value: formatSalary(row.medianSalary),
                },
                {
                    label: 'Örneklem',
                    value: formatSample(row.sampleCount),
                },
            ]}
        />
    );
};

const CompanyLabel = ({ x, y, width, value, mobile }: any) => (
    <text
        x={x + width / 2}
        y={Math.max(14, y - 10)}
        textAnchor="middle"
        fill="#5e655f"
        fontSize={mobile ? 8 : 10}
        fontFamily="DM Mono, monospace"
    >
        {formatCompactSalary(Number(value))}
    </text>
);

const CompanySizeImpact: React.FC<CompanySizeImpactProps> = ({
    rows,
    selectedYear,
}) => {
    const [seniority, setSeniority] = useState<Seniority>('Senior');
    const mobile = useMediaQuery('(max-width: 680px)');
    const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
    const chartRows = useMemo(
        () =>
            sortCompanySizeRows(
                rows.filter(
                    (row) =>
                        row.year === selectedYear &&
                        row.seniority === seniority
                )
            ),
        [rows, selectedYear, seniority]
    );
    const insight = useMemo(
        () => buildCompanyInsight(chartRows, seniority),
        [chartRows, seniority]
    );

    return (
        <>
            <div className="section-control">
                <div
                    className="segmented-control"
                    role="group"
                    aria-label="Kıdem seviyesi"
                >
                    {SENIORITY_ORDER.map((option) => (
                        <button
                            key={option}
                            type="button"
                            className={
                                seniority === option ? 'is-active' : ''
                            }
                            aria-pressed={seniority === option}
                            onClick={() => setSeniority(option)}
                        >
                            {option}
                        </button>
                    ))}
                </div>
            </div>

            <div
                className="chart-panel chart-panel--company"
                role="img"
                aria-label={`${seniority} seviyesi için şirket büyüklüğüne göre medyan maaş`}
            >
                <div className="chart-canvas" aria-hidden="true">
                    <ResponsiveContainer width="100%" height={440}>
                        <BarChart
                            data={chartRows}
                            margin={{
                                top: 40,
                                right: mobile ? 4 : 20,
                                bottom: mobile ? 50 : 12,
                                left: 4,
                            }}
                        >
                            <CartesianGrid
                                vertical={false}
                                stroke="#d9d5ca"
                                strokeDasharray="2 5"
                            />
                            <XAxis
                                dataKey="companySize"
                                axisLine={false}
                                tickLine={false}
                                interval={0}
                                angle={mobile ? -38 : 0}
                                textAnchor={mobile ? 'end' : 'middle'}
                                height={mobile ? 70 : 34}
                                tickFormatter={(value) =>
                                    mobile
                                        ? value.replace(' Kişi', '')
                                        : value
                                }
                                tick={{
                                    fill: '#5e655f',
                                    fontSize: mobile ? 9 : 10,
                                    fontWeight: 500,
                                }}
                                dy={mobile ? 10 : 8}
                            />
                            <YAxis
                                axisLine={false}
                                tickLine={false}
                                tickFormatter={formatCompactSalary}
                                tick={{
                                    fill: '#737a74',
                                    fontSize: mobile ? 9 : 10,
                                    fontFamily: 'DM Mono, monospace',
                                }}
                                width={mobile ? 56 : 70}
                            />
                            <Tooltip
                                cursor={{ fill: 'rgba(38, 67, 56, 0.05)' }}
                                content={<CompanyTooltip />}
                            />
                            <Bar
                                dataKey="medianSalary"
                                fill="#c85e38"
                                radius={[7, 7, 0, 0]}
                                maxBarSize={64}
                                isAnimationActive={!reduceMotion}
                                animationDuration={650}
                            >
                                <LabelList
                                    dataKey="medianSalary"
                                    content={(props) => (
                                        <CompanyLabel
                                            {...props}
                                            mobile={mobile}
                                        />
                                    )}
                                />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
                <AccessibleDataTable
                    caption={`${seniority} şirket büyüklüğü ve maaş verileri`}
                    headers={[
                        'Şirket büyüklüğü',
                        'Medyan maaş',
                        'Örneklem',
                    ]}
                    rows={chartRows.map((row) => [
                        row.companySize,
                        formatSalary(row.medianSalary),
                        formatSample(row.sampleCount),
                    ])}
                />
            </div>

            <div className="insight-card insight-card--bottom">
                <span aria-hidden="true">↗</span>
                <p>{insight}</p>
            </div>
        </>
    );
};

export default React.memo(CompanySizeImpact);
